import React, { createContext, type ReactNode, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { getScreenKeyFromPath } from '@/config/screen-mode-config';
import { useScreenModes } from './ScreenModesProvider';

export type AppMode = 'demo' | 'live';

interface ModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  isDemo: boolean;
  isLive: boolean;
  toggleMode: () => void;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

interface ModeProviderProps {
  children: ReactNode;
}

/**
 * ModeProvider that derives the current mode from the per-screen modes
 * stored in ScreenModesProvider, based on the current route.
 *
 * All existing consumers (useModeAwareQuery, etc.) continue
 * to call useMode() and get the correct per-screen mode.
 */
export function ModeProvider({ children }: Readonly<ModeProviderProps>) {
  const location = useLocation();
  const { getScreenMode, setScreenMode } = useScreenModes();

  const screenKey = getScreenKeyFromPath(location.pathname);
  const mode: AppMode = screenKey ? getScreenMode(screenKey) : 'demo';

  const setMode = (newMode: AppMode) => {
    if (screenKey) {
      setScreenMode(screenKey, newMode);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'demo' ? 'live' : 'demo');
  };

  const value: ModeContextType = {
    mode,
    setMode,
    isDemo: mode === 'demo',
    isLive: mode === 'live',
    toggleMode,
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode() {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}
